import { GraphQLClient, gql } from 'graphql-request';
import type {
  CompleteQuarterlyProgressReportReviewByIdInput,
  MarkQuarterlyProgressReportReviewOpenedByIdInput,
  Mutation,
  Query,
  RequestQuarterlyProgressReportReviewInput,
  ReviewQuarterlyProgressReportReviewSectionByIdInput,
} from '../generated/graphql';

const QuarterlyProgressReportReviewFields = `
  id
  firmID
  firmClientID
  householdID
  periodStart
  periodEnd
  status
  lastRequestedAt
  assetsReviewedAt
  debtsReviewedAt
  spendingReviewedAt
  assetsSnapshot {
    reviewedAt
    changedAccountCount
    currentTotal {
      value
      currencyCode
    }
    priorQuarterTotal {
      value
      currencyCode
    }
  }
  debtsSnapshot {
    reviewedAt
    changedAccountCount
    currentTotal {
      value
      currencyCode
    }
    priorQuarterTotal {
      value
      currencyCode
    }
  }
  spendingSnapshot {
    reviewedAt
    changedAccountCount
    currentTotal {
      value
      currencyCode
    }
    priorQuarterTotal {
      value
      currencyCode
    }
  }
  clientReviewStartedAt
  clientReviewCompletedAt
  createdAt
  updatedAt
`;

export const currentQuarterlyProgressReportReviewQuery = gql`
  query CurrentQuarterlyProgressReportReview($firmClientID: ID!) {
    currentQuarterlyProgressReportReview(firmClientID: $firmClientID) {
      ${QuarterlyProgressReportReviewFields}
    }
  }
`;

export const quarterlyProgressReportReviewQuery = gql`
  query QuarterlyProgressReportReview($firmClientID: ID!) {
    quarterlyProgressReportReview(firmClientID: $firmClientID) {
      ${QuarterlyProgressReportReviewFields}
    }
  }
`;

export const quarterlyProgressReportReviewByIDQuery = gql`
  query QuarterlyProgressReportReviewByID($firmClientID: ID!, $reviewID: ID!) {
    quarterlyProgressReportReviewByID(firmClientID: $firmClientID, reviewID: $reviewID) {
      ${QuarterlyProgressReportReviewFields}
    }
  }
`;

export const markQuarterlyProgressReportReviewOpenedByIDMutation = gql`
  mutation MarkQuarterlyProgressReportReviewOpenedByID($input: MarkQuarterlyProgressReportReviewOpenedByIDInput!) {
    markQuarterlyProgressReportReviewOpenedByID(input: $input) {
      review {
        ${QuarterlyProgressReportReviewFields}
      }
    }
  }
`;

export const reviewQuarterlyProgressReportReviewSectionByIDMutation = gql`
  mutation ReviewQuarterlyProgressReportReviewSectionByID(
    $input: ReviewQuarterlyProgressReportReviewSectionByIDInput!
  ) {
    reviewQuarterlyProgressReportReviewSectionByID(input: $input) {
      review {
        ${QuarterlyProgressReportReviewFields}
      }
    }
  }
`;

export const completeQuarterlyProgressReportReviewByIDMutation = gql`
  mutation CompleteQuarterlyProgressReportReviewByID($input: CompleteQuarterlyProgressReportReviewByIDInput!) {
    completeQuarterlyProgressReportReviewByID(input: $input) {
      review {
        ${QuarterlyProgressReportReviewFields}
      }
    }
  }
`;

export const requestQuarterlyProgressReportReviewMutation = gql`
  mutation RequestQuarterlyProgressReportReview($input: RequestQuarterlyProgressReportReviewInput!) {
    requestQuarterlyProgressReportReview(input: $input) {
      created
      eligibleRecipientCount
      emailNotificationRequestCount
      pushNotificationRequestCount
      review {
        ${QuarterlyProgressReportReviewFields}
      }
    }
  }
`;

export const clientGetCurrentQuarterlyProgressReportReview = async (
  client: GraphQLClient,
  args: { firmClientID: string }
) => {
  const response = await client.request<Pick<Query, 'currentQuarterlyProgressReportReview'>>(
    currentQuarterlyProgressReportReviewQuery,
    args
  );

  if (!response.currentQuarterlyProgressReportReview) {
    throw new Error('Current Quarterly Progress Report review was not returned.');
  }

  return response.currentQuarterlyProgressReportReview;
};

export const clientGetQuarterlyProgressReportReview = async (client: GraphQLClient, args: { firmClientID: string }) => {
  const response = await client.request<Pick<Query, 'quarterlyProgressReportReview'>>(
    quarterlyProgressReportReviewQuery,
    args
  );

  return response.quarterlyProgressReportReview;
};

export const clientGetQuarterlyProgressReportReviewByID = async (
  client: GraphQLClient,
  args: { firmClientID: string; reviewID: string }
) => {
  const response = await client.request<Pick<Query, 'quarterlyProgressReportReviewByID'>>(
    quarterlyProgressReportReviewByIDQuery,
    args
  );

  return response.quarterlyProgressReportReviewByID;
};

export const clientMarkQuarterlyProgressReportReviewOpenedByID = async (
  client: GraphQLClient,
  input: MarkQuarterlyProgressReportReviewOpenedByIdInput
) => {
  const response = await client.request<Pick<Mutation, 'markQuarterlyProgressReportReviewOpenedByID'>>(
    markQuarterlyProgressReportReviewOpenedByIDMutation,
    { input }
  );

  const review = response.markQuarterlyProgressReportReviewOpenedByID?.review;
  if (!review) {
    throw new Error('Opened Quarterly Progress Report review was not returned.');
  }

  return review;
};

export const clientReviewQuarterlyProgressReportReviewSectionByID = async (
  client: GraphQLClient,
  input: ReviewQuarterlyProgressReportReviewSectionByIdInput
) => {
  const response = await client.request<Pick<Mutation, 'reviewQuarterlyProgressReportReviewSectionByID'>>(
    reviewQuarterlyProgressReportReviewSectionByIDMutation,
    { input }
  );

  const review = response.reviewQuarterlyProgressReportReviewSectionByID?.review;
  if (!review) {
    throw new Error(`Reviewed Quarterly Progress Report review was not returned for section ${input.section}.`);
  }

  return review;
};

export const clientCompleteQuarterlyProgressReportReviewByID = async (
  client: GraphQLClient,
  input: CompleteQuarterlyProgressReportReviewByIdInput
) => {
  const response = await client.request<Pick<Mutation, 'completeQuarterlyProgressReportReviewByID'>>(
    completeQuarterlyProgressReportReviewByIDMutation,
    { input }
  );

  const review = response.completeQuarterlyProgressReportReviewByID?.review;
  if (!review) {
    throw new Error('Completed Quarterly Progress Report review was not returned.');
  }

  return review;
};

export const advisorRequestQuarterlyProgressReportReview = async (
  client: GraphQLClient,
  input: RequestQuarterlyProgressReportReviewInput
) => {
  const response = await client.request<Pick<Mutation, 'requestQuarterlyProgressReportReview'>>(
    requestQuarterlyProgressReportReviewMutation,
    { input }
  );

  return response.requestQuarterlyProgressReportReview;
};
